import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FormState, FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import Heading from '@components/Heading/Heading';
import Select from '@components/Select/Select';
import Input from '@components/Input/Input';
import Button from '@components/Button/Button';
import Icon from '@components/icons/Icon';
import OrderPositions from '@features/userPanel/components/Orders/OrderPositions';
import NextSurveyApi from '@features/api/nextApiService/nextSurveyApi';
import alertsStore from '@context/global/mobx/alertStore';
import { PositionType } from '@features/userPanel/typescript';
import { min, max } from '@features/userPanel/mobx/stores/validators';
import { RETURN_REASON_KEYS, TReturnReasonKey } from '@features/userPanel/components/ProductReturn/const/returnReasons';
import styles from '@features/userPanel/components/ProductReturn/ProductReturnForm.styles';
export type TProductReturnFormData = {
  orderId: string;
  orderNumber: string;
  customerId: string;
};
type PropsType = {
  products: Array<PositionType>;
  orderData: TProductReturnFormData;
  isStorAssemblyAvailable?: boolean;
  isNarrowerThanTabletlg?: boolean;
  onBack?: VoidFunction;
};
const ProductReturnForm = ({
  products,
  orderData,
  isStorAssemblyAvailable = false,
  isNarrowerThanTabletlg = false,
  onBack
}: PropsType) => {
  const {
    t
  } = useTranslation('user');
  const {
    t: commonT
  } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<Array<string>>([]);
  const [showProductSelectionError, setShowProductSelectionError] = useState(false);
  const returnForm = useMemo(() => new FormState({
    returnReason: new FieldState<string>('').disableAutoValidation(),
    comment: new FieldState<string>('').validators(min(5), max(4000)).disableAutoValidation()
  }), []);
  const returnReasonOptions = useMemo(() => RETURN_REASON_KEYS.map(reasonKey => ({
    name: t(`orders.return.reasons.${reasonKey}`),
    value: reasonKey
  })), [t]);
  const toggleProductSelection = (positionId: string) => {
    setShowProductSelectionError(false);
    setSelectedProductIds(currentSelection => currentSelection.includes(positionId) ? currentSelection.filter(id => id !== positionId) : [...currentSelection, positionId]);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setShowProductSelectionError(false);
    try {
      if (selectedProductIds.length === 0) {
        setShowProductSelectionError(true);
        setIsSubmitting(false);
        return;
      }
      const validationResult = await returnForm.validate();
      if (validationResult.hasError) {
        setIsSubmitting(false);
        return;
      }
      const returnReasonKey = returnForm.$.returnReason.value as TReturnReasonKey | '';
      const returnReason = returnReasonKey ? t(`orders.return.reasons.${returnReasonKey}`) : undefined;
      const comment = returnForm.$.comment.value.trim();
      const selectedProducts = products.filter(product => selectedProductIds.includes(product.positionId)).map(product => ({
        name: product.name,
        ...(product.configuration?.description && {
          color: product.configuration.description
        }),
        ...(product.image && {
          image: product.image
        }),
        number: product.configuration?.erpProductId?.toString() || product.configuration?.pimProductId?.toString() || product.positionId
      }));
      const response = await NextSurveyApi.submitReturnRequest({
        customerId: orderData.customerId,
        orderId: orderData.orderId,
        ...(returnReason && {
          returnReason
        }),
        ...(comment && {
          comment
        }),
        product: selectedProducts
      });
      if (response.error) {
        alertsStore.pushAlert({
          message: response.message ? commonT(response.message) : t('orders.return.error.submitFailed'),
          display: true
        });
        setIsSubmitting(false);
        return;
      }
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting return request:', error);
      alertsStore.pushAlert({
        message: t('orders.return.error.submitFailed'),
        display: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div sx={styles.mainContainer}>
        <div sx={styles.successContainer}>
          <div sx={styles.successIconContainer}>
            <div sx={styles.successIcon}>
              <Icon icon="Check" size="48px" color="darkGrey700" />
            </div>
          </div>
          <Heading h={4} headingType={4} addSx={styles.successTitle}>
            {t('orders.return.success.title')}
          </Heading>
          <p sx={styles.successMessage}>{t('orders.return.success.message')}</p>
        </div>
        <div sx={styles.successFooter}>
          <Button variant="default" variantColor="dark" onClick={onBack} fullWidth={isNarrowerThanTabletlg}>
            {t('orders.return.success.backButton')}
          </Button>
        </div>
      </div>;
  }
  return <div sx={styles.mainContainer}>
      <div sx={styles.scrollableContent}>
        {/* <div sx={styles.infoContainer}>
          <p sx={styles.infoText}>{t('orders.return.infoText')}</p>
         </div> */}

        <Heading h={4} headingType={4} align="left" addSx={styles.sectionTitle}>
          {t('orderedProducts')}
        </Heading>

        <OrderPositions positions={products} isStorAssemblyAvailable={isStorAssemblyAvailable} selectable selectedPositionIds={selectedProductIds} onTogglePosition={toggleProductSelection} />

        {showProductSelectionError && <div sx={styles.selectionError}>{t('orders.return.error.productRequired')}</div>}

        <div sx={styles.filler} />

        <Heading h={4} headingType={4} align="left" addSx={styles.sectionTitle}>
          {t('orders.return.reasonHeading')}
        </Heading>

        <div sx={styles.reasonSelectWrapper}>
          <Select value={returnForm.$.returnReason.value} onChange={value => returnForm.$.returnReason.onChange(value.toString())} options={returnReasonOptions} placeholder={t('orders.return.reasonPlaceholder')} addSx={styles.reasonSelect} showError={returnForm.$.returnReason.hasError} error={returnForm.$.returnReason.error || ''} cy="ReturnReasonSelect" />
        </div>

        <Heading h={4} headingType={4} align="left" addSx={styles.sectionTitle}>
          {t('orders.return.commentHeading')}
        </Heading>

        <div sx={styles.commentContainer}>
          <Input field={returnForm.$.comment} placeholder="returnForm.commentPlaceholder" bottomDescription={t('orders.return.commentCaption')} maxLength={4000} cy="ReturnCommentInput" />
        </div>

        <div sx={styles.submitButtonContainer}>
          <Button variant="default" variantColor="brand" onClick={handleSubmit} disabled={isSubmitting} fullWidth addSx={styles.submitButton} cy="SubmitReturnRequestButton">
            {t('orders.return.submitButton')}
          </Button>
        </div>
      </div>
    </div>;
};
export default observer(ProductReturnForm);